import { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { State } from 'reducers';
import { push as navigateTo } from 'connected-react-router';
import { useTranslation } from 'react-i18next';
import { disableDeviceRequest } from 'actions/deviceActions';
import { publishDeviceScriptRequest } from 'actions/deviceActions';
import { getBuilding } from 'selectors/buildingsSelectors';
import { parse } from 'logic/deviceScriptParser';
import { InfoMessage } from 'ui/shared/messages';
import { BuildingId, DeviceClass, DeviceFamily, DeviceId, DeviceType } from 'shared/models';
import { EditFileDialog } from '../editFileDialog/EditFileDialog';
import { Permission } from 'shared/permissions';
import { useSession } from 'hooks';

require('./QuickDeviceCommands.css');

export type Props = {
    buildingId: BuildingId;
    deviceId: DeviceId;
    isDown: boolean;
    isEcsEnabled: boolean;
    nextCheckIn: number | null;
    deviceType: DeviceType | null;
    originalScript: string | null;
};

const checkInDeviceClasses = [ DeviceClass.Gateway, DeviceClass.Display, DeviceClass.LobbyPanel ];

const getLogsScript = '{"commands":["get logs"],"checkin":900}';
const resetScript = '{"commands":["restart system"],"checkin":900}';
const getScreenScript = '{"commands":["get screen"],"checkin":900}';

export const QuickDeviceCommands = (props: Props) => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();

    const { buildingId, deviceId, isDown, isEcsEnabled, deviceType, originalScript } = props;

    const building = useSelector((state: State) => buildingId ? getBuilding(state, props) : null);
    const { urlSlug = null } = building || {};
    const isLegacyMatisse = !!(deviceType && (
        deviceType.deviceCapabilities.Matisse
        && deviceType.displayName.toLowerCase().indexOf('matisse') >= 0
        && deviceType.displayName.toLowerCase().indexOf('matisse r5') < 0
        && deviceType.deviceFamily !== DeviceFamily.MatisseR5
    ));

    const remoteUpdateDisabled = !!building?.properties?.remoteUpdateDisabled;
    const deviceScript = parse(originalScript, remoteUpdateDisabled);
    const isGettingLogs = deviceScript.containsCommand('get logs');
    const isRestarting = deviceScript.containsCommand('restart system');
    const isCapturingScreen = deviceScript.containsCommand('get screen');
    const deviceCanCheckin = deviceType && checkInDeviceClasses.includes(deviceType.deviceClass);

    const [ editingFilename, setEditingFilename ] = useState<string | null>(null);

    const dispatch = useDispatch();

    const onClickEdit = useCallback(() => {
        dispatch(navigateTo(`/buildings/${urlSlug}/devices/${deviceId}/status/edit`));
    }, [ dispatch, urlSlug, deviceId ]);

    const onClickGetLogs = useCallback(() => {
        dispatch(publishDeviceScriptRequest(buildingId!, deviceId, getLogsScript, 'device'));
    }, [ dispatch, buildingId, deviceId ]);

    const onClickReset = useCallback(() => {
        dispatch(publishDeviceScriptRequest(buildingId!, deviceId, resetScript, 'device'));
    }, [ dispatch, buildingId, deviceId ]);

    const onClickDisable = useCallback(() => {
        dispatch(disableDeviceRequest(buildingId!, deviceId));
    }, [ dispatch, buildingId, deviceId ]);

    const onClickCapture = useCallback(() => {
        dispatch(publishDeviceScriptRequest(buildingId!, deviceId, getScreenScript, 'device'));
    }, [ dispatch, buildingId, deviceId ]);
    
    const handleClickEditTemplateDgt = useCallback(() => {
        setEditingFilename('/home/dmg/config/templates/template.dgt');
    }, [ setEditingFilename ]);
    
    const handleClickEditSettingsXml = useCallback(() => {
        setEditingFilename('/home/dmg/config/settings.xml');
    }, [ setEditingFilename ]);

    const handleClickEditSshSettingsXml = useCallback(() => {
        setEditingFilename('/home/dmg/config/ssh_settings.xml');
    }, [ setEditingFilename ]);


    if (!buildingId || !hasPermission(buildingId, Permission.ViewDevices)) {
        return null;
    }
    return (
        <div id="quick-device-commands">
            <h3>
                { t('DEVICE.STATUS.QUICK_COMMANDS')}
            </h3>
            { hasPermission(buildingId, Permission.EditDevice) &&
                <button
                    id="qdc-edit"
                    type="button"
                    className="ui small basic button"
                    onClick={ onClickEdit }
                >
                    { t("COMMAND.EDIT_DEVICE") }
                </button>
            }
            { false && // TODO: need to think more about the design for this
                <button
                    id="qdc-capture"
                    type="button"
                    className="ui small basic button"
                    disabled={ isCapturingScreen }
                    onClick={ onClickCapture }
                >
                    { t("COMMAND.CAPTURE_SCREENSHOT") }
                </button>
            }
            { deviceCanCheckin && hasPermission(buildingId, Permission.CommandDevice) &&
                <button
                    id="qdc-logs"
                    type="button"
                    className="ui small basic button"
                    disabled={ isGettingLogs }
                    onClick={ onClickGetLogs }
                >
                    { t("COMMAND.GET_LOGS") }
                </button>
            }
            { deviceCanCheckin && hasPermission(buildingId, Permission.CommandDevice) &&
                <button
                    id="qdc-reset"
                    type="button"
                    className="ui small basic button"
                    disabled={ isRestarting }
                    onClick={ onClickReset }
                >
                    { t("COMMAND.RESET_DEVICE") }
                </button>
            }
            { isDown && hasPermission(buildingId, Permission.CommandDevice) &&
                <button
                    id="qdc-mark-offline"
                    type="button"
                    className="ui small basic button"
                    onClick={ onClickDisable }
                >
                    { t("COMMAND.MARK_AS_OFFLINE") }
                </button>
            }
            { hasPermission(buildingId, Permission.AdminDevice) && isEcsEnabled && isLegacyMatisse &&
                <button
                    id="qdc-edit-template-dgt"
                    type="button"
                    className="ui small basic button"
                    onClick={ handleClickEditTemplateDgt }
                >
                    { t("COMMAND.EDIT_X", [ "template.dgt" ]) }
                </button>
            }
            { hasPermission(buildingId, Permission.AdminDevice) && isEcsEnabled && isLegacyMatisse &&
                <button
                    id="qdc-edit-settings-xml"
                    type="button"
                    className="ui small basic button"
                    onClick={ handleClickEditSettingsXml }
                >
                    { t("COMMAND.EDIT_X", [ "settings.xml" ]) }
                </button>
            }
            { hasPermission(buildingId, Permission.AdminDevice) && isEcsEnabled && isLegacyMatisse &&
                <button
                    id="qdc-edit-ssh-settings-xml"
                    type="button"
                    className="ui small basic button"
                    onClick={ handleClickEditSshSettingsXml }
                >
                    { t("COMMAND.EDIT_X", [ "ssh_settings.xml" ]) }
                </button>
            }
            <div>
                { isCapturingScreen &&
                    <InfoMessage message={ t('DEVICE.STATUS.PENDING_CAPTURE') }/>
                }
                { isGettingLogs &&
                    <InfoMessage message={ t('DEVICE.STATUS.PENDING_LOGS') }/>
                }
                { isRestarting &&
                    <InfoMessage message={ t('DEVICE.STATUS.PENDING_RESTART') }/>
                }
            </div>

            { hasPermission(buildingId, Permission.AdminDevice) &&
                <EditFileDialog
                    deviceId={ deviceId }
                    visible={ !!editingFilename }
                    filename={ editingFilename }
                    readOnly={ remoteUpdateDisabled }
                    onClose={ () => setEditingFilename(null) }
                />
            }
        </div>
    );
};
